/** VIEW WHAT THEY DID — the steps of one sign-in, in order.
 *
 *  Opened from a Login row (or a user's name) on the Audit Logs page. Super
 *  admins only; the button itself is absent for partner accounts.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Badge } from '../ui/badge';
import { Activity, Ban, AlertTriangle, Eye, X } from 'lucide-react';
import api from '../../lib/api';

const TONE = {
    refused: { icon: Ban, className: 'text-rose-600 dark:text-rose-400', badge: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' },
    failed: { icon: AlertTriangle, className: 'text-amber-600 dark:text-amber-400', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
    done: { icon: Activity, className: 'text-slate-600 dark:text-slate-300', badge: '' },
};

export const WhatTheyDidPanel = ({ target, onClose }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState('');

    useEffect(() => {
        if (!target) { setData(null); return; }
        let live = true;
        setLoading(true);
        setFailed('');
        const query = target.session_id
            ? `session_id=${encodeURIComponent(target.session_id)}`
            : `user_id=${encodeURIComponent(target.user_id || '')}`;
        api.get(`/admin/user-actions?${query}`)
            .then((res) => { if (live) setData(res.data); })
            .catch((err) => {
                // Never disguise a failure as "did nothing".
                if (live) setFailed(err?.response?.status ? `HTTP ${err.response.status}` : 'network');
            })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
    }, [target]);

    const steps = data?.steps || [];

    return (
        <Sheet open={!!target} onOpenChange={(open) => { if (!open) onClose(); }}>
            <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="what-they-did-panel">
                <SheetHeader className="pb-4">
                    <SheetTitle className="flex items-center gap-2">
                        <Activity className="w-4 h-4 text-primary" />
                        {t('auditLogs.viewWhatTheyDid', 'View what they did')}
                    </SheetTitle>
                    <p className="text-sm text-muted-foreground" data-testid="what-they-did-subject">
                        {target?.email}
                        {data?.total ? ` · ${data.total} ${t('auditLogs.steps', 'steps')}` : ''}
                    </p>
                </SheetHeader>

                {loading && (
                    <p className="text-sm text-muted-foreground" data-testid="what-they-did-loading">
                        {t('common.loading', 'Loading…')}
                    </p>
                )}

                {!loading && failed && (
                    <p className="text-sm text-rose-600 dark:text-rose-400" data-testid="what-they-did-error">
                        {t('auditLogs.stepsUnavailable', 'Could not load the steps for this sign-in')} ({failed})
                    </p>
                )}

                {!loading && !failed && steps.length === 0 && (
                    <p className="text-sm text-muted-foreground" data-testid="what-they-did-empty">
                        {t('auditLogs.nothingButSignIn', 'This person signed in and did nothing else.')}
                    </p>
                )}

                {!loading && !failed && steps.length > 0 && (
                    <ol className="relative border-l border-border pl-5 space-y-4" data-testid="what-they-did-steps">
                        {steps.map((step, i) => {
                            const tone = TONE[step.outcome] || TONE.done;
                            const Icon = step.kind === 'view' ? Eye : tone.icon;
                            return (
                                <li key={i} className="relative" data-testid={`what-they-did-step-${i}`}>
                                    <span className="absolute -left-[27px] top-1 bg-background p-0.5">
                                        <Icon className={`w-3.5 h-3.5 ${tone.className}`} />
                                    </span>
                                    <div className="flex items-start justify-between gap-3">
                                        <p className={`text-sm font-medium ${step.outcome === 'refused' ? tone.className : ''}`}>
                                            {step.text}
                                            {step.repeat_count > 1 && (
                                                <span className="ml-1 text-xs text-muted-foreground">×{step.repeat_count}</span>
                                            )}
                                        </p>
                                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                                            {String(step.timestamp).slice(11, 19)}
                                        </span>
                                    </div>
                                    {step.outcome === 'refused' && (
                                        <Badge variant="secondary" className={`mt-1 text-[10px] ${tone.badge}`} data-testid={`what-they-did-refused-${i}`}>
                                            {t('auditLogs.blocked', 'Blocked')}
                                        </Badge>
                                    )}
                                    {Object.keys(step.changes || {}).length > 0 && (
                                        <p className="mt-1 text-xs text-muted-foreground break-words">
                                            {Object.entries(step.changes).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                                        </p>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                )}
            </SheetContent>
        </Sheet>
    );
};
